import { useMemo, useState } from "react";
import { Table, Form } from "antd";
import EditableCell from "./EditableCell";
import FilterDropdown from "./FilterDropdown";
import ActionColumn from "./ActionColumn";
import AddNewRowButton from "./AddNewRowButton";
import { useEditableTable } from "hooks/useEditableTable";
import {
  TableCommonProps,
  TableRecord,
  InputType,
  FilterType,
} from "types/table.types";
import type {
  ColumnsType,
  ColumnType,
  TablePaginationConfig,
} from "antd/es/table";
import { Funnel } from "@phosphor-icons/react";
import { FilterValue } from "antd/es/table/interface";

const TableEditCommon = <T extends TableRecord>({
  columns,
  data,
  onChangeData,
  onAddRow,
  onDeleteRow,
  onSave,
  loading = false,
  pagination = false,
  rowSelection = false,
  addRowText = "Thêm mới",
  bordered = false,
  type,
  editable = true,
  onApprove,
  onReject,
  newRowDefaults,
}: TableCommonProps<T>) => {
  const {
    form,
    dataSource,
    isEditing,
    edit,
    cancel,
    save,
    addRow,
    deleteRow,
    handleDoubleClick,
  } = useEditableTable<T>(data, onChangeData, onSave);

  const [filteredInfo, setFilteredInfo] = useState<
    Record<string, FilterValue | null>
  >({});

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>
    // sorter: SorterResult<T> | SorterResult<T>[]
  ) => {
    setFilteredInfo(filters);
  };

  const clearAllFilters = () => {
    setFilteredInfo({});
  };

  const mergedColumns: ColumnsType<T> = useMemo(() => {
    const cols: ColumnsType<T> = columns.map((col) => {
      const columnKey = col.key || (col.dataIndex as string);
      const isFiltered =
        filteredInfo[columnKey] && filteredInfo[columnKey]!.length > 0;

      const baseColumn: ColumnType<T> = {
        title: col.title,
        dataIndex: col.dataIndex as string,
        key: columnKey,
        align: col.align,
        fixed: col.fixed,
        ellipsis: true,
        render: col.render,
        sorter: col.sorter
          ? typeof col.sorter === "function"
            ? col.sorter
            : (a: T, b: T) => {
                const aVal = a[col.dataIndex as string];
                const bVal = b[col.dataIndex as string];
                if (typeof aVal === "string" && typeof bVal === "string") {
                  return aVal.localeCompare(bVal);
                }
                if (typeof aVal === "number" && typeof bVal === "number") {
                  return aVal - bVal;
                }
                return 0;
              }
          : undefined,
        filterIcon: col.filterable ? (
          isFiltered ? (
            <Funnel
              className="text-[var(--primaryColor500)]"
              weight="fill"
              size={16}
            />
          ) : (
            <Funnel className="text-[var(--textBlack56)]" size={16} />
          )
        ) : undefined,
        filterDropdown: col.filterable
          ? (props) => (
              <FilterDropdown
                {...props}
                filterType={col.filterType || FilterType.SEARCH}
                filterOptions={col.filterOptions}
              />
            )
          : undefined,
        onFilter: col.filterable
          ? (value, record) => {
              const recordValue = record[col.dataIndex as keyof T];
              const filterType = col.filterType || FilterType.SEARCH;

              if (
                filterType === FilterType.SELECT ||
                filterType === FilterType.MULTI_SELECT
              ) {
                // Exact match cho select
                return String(recordValue) === String(value);
              } else {
                // Contains search cho input
                return String(recordValue)
                  .toLowerCase()
                  .includes(String(value).toLowerCase());
              }
            }
          : undefined,
        filteredValue: filteredInfo[columnKey] || null,
      };

      if (col.editable) {
        return {
          ...baseColumn,
          onCell: (record: T) => ({
            record,
            inputType: col.inputType || InputType.TEXT,
            dataIndex: col.dataIndex,
            title: col.title,
            editing: isEditing(record),
            selectOptions: col.selectOptions,
            rules: col.rules,
          }),
        };
      }

      return baseColumn;
    });

    // Add action column với fixed right
    cols.push({
      title: "Action",
      key: "action",
      fixed: "right",
      align: "center",
      width: 72,
      render: (_: unknown, record: T) => (
        <ActionColumn
          record={record}
          isEditing={isEditing(record)}
          edit={() => edit(record)}
          save={save}
          cancel={cancel}
          deleteRow={(key) => {
            deleteRow(key);
            onDeleteRow?.(key);
          }}
          type={type}
          onApprove={onApprove}
          onReject={onReject}
        />
      ),
    });

    return cols;
  }, [
    columns,
    isEditing,
    edit,
    save,
    cancel,
    deleteRow,
    onDeleteRow,
    filteredInfo,
  ]);

  const handleAddRow = async () => {
    clearAllFilters();
    const newRow = { ...newRowDefaults } as Partial<T>;
    const success = await addRow(newRow);
    if (success) {
      onAddRow?.(newRow);
    }
  };

  return (
    <>
      <div className="relative">
        <Form form={form} component={false} disabled={!editable}>
          <Table<T>
            className="table-edit-common-compact"
            size="small"
            bordered={bordered}
            components={{
              body: {
                cell: EditableCell,
              },
            }}
            dataSource={dataSource}
            columns={mergedColumns}
            rowClassName="editable-row"
            pagination={
              pagination
                ? {
                    showSizeChanger: true,
                    showTotal: (total) => `Tổng ${total} bản ghi`,
                    defaultPageSize: 10,
                    pageSizeOptions: ["10", "20", "50", "100"],
                  }
                : false
            }
            loading={loading}
            scroll={{ x: "max-content" }}
            footer={() => (
              <AddNewRowButton onAdd={handleAddRow} text={addRowText} />
            )}
            onRow={(record) => ({
              onDoubleClick: () => handleDoubleClick(record),
            })}
            rowSelection={
              rowSelection
                ? {
                    type: "checkbox",
                    fixed: true,
                  }
                : undefined
            }
            onChange={handleTableChange}
          />
          {!editable && (
            <div className="absolute inset-0 z-10 bg-transparent cursor-not-allowed" />
          )}
        </Form>
      </div>
    </>
  );
};

export default TableEditCommon;
